import type { CurrencyParam, PlansDuration } from '~/pages/PricePage/types';
import type { Pricing } from '~/types';
import { areStringsEqual } from '~/util/areStringsEqual';
import { throwOnlyInDev } from '~/util/throwOnlyInDev';

type PlanFilters = {
  currency: CurrencyParam;
  duration: PlansDuration;
};

const pricingDurationsIDsMap: Record<PlansDuration, string> = {
  monthly: '1',
  annual: '2',
};

function safelyFilterPlansByDuration(plans: Pricing[], filters: Pick<PlanFilters, 'duration'>) {
  const hasPlansWithRequestedDuration = plans.some(
    (plan) =>
      !Boolean(plan.enterprise) &&
      plan.pricing_duration.id === pricingDurationsIDsMap[filters.duration],
  );

  // fallback to first existing duration if no filters.duration plans found
  const durationId = hasPlansWithRequestedDuration
    ? pricingDurationsIDsMap[filters.duration]
    : plans[0].pricing_duration.id;

  return plans.filter((plan) => {
    return Boolean(plan.enterprise) || plan.pricing_duration.id === durationId;
  });
}

function createCurrencyFilter(currency: CurrencyParam) {
  return (plan: Pricing) => {
    const isEnterprise = Boolean(plan.enterprise);
    return (
      isEnterprise ||
      areStringsEqual(plan.chargebee_plan_info?.currency_code, currency, {
        ignoreCase: true,
      })
    );
  };
}

function safelyFilterPlansByCurrency(plans: Pricing[], filters: Pick<PlanFilters, 'currency'>) {
  const plansWithRequestedCurrency = plans.filter(createCurrencyFilter(filters.currency));
  if (plansWithRequestedCurrency.filter((plan) => !plan.enterprise).length > 0) {
    return plansWithRequestedCurrency;
  }

  // if there are no plans with provided currency, try to get ones with gbp
  const gbpPlans = plans.filter(createCurrencyFilter('gbp'));
  if (gbpPlans.filter((plan) => !plan.enterprise).length > 0) {
    return gbpPlans;
  }

  // fallback to some currency that we have
  const currency = plans[0].chargebee_plan_info?.currency_code?.toLowerCase();

  if (!currency) {
    throwOnlyInDev(new Error(`There are no currency in chargebee_plan_info for ${plans[0].id}`));
    return [];
  }

  return plans.filter(createCurrencyFilter(currency as CurrencyParam));
}

/**
 * Filter plans by provided filters. Fallbacks to default filters if no plans found with provided ones
 * @param plans plans before filter
 * @param filters pricing plan filters
 * @returns filtered plans
 */
export function safelyFilterPricePlans(plans: Pricing[], filters: PlanFilters) {
  if (!plans || plans.length === 0) {
    return [];
  }

  const filteredByDurationPlans = safelyFilterPlansByDuration(plans, filters);
  const filteredByCurrencyPlans = safelyFilterPlansByCurrency(filteredByDurationPlans, filters);

  return filteredByCurrencyPlans;
}
